import React from "react";

import {
  AlertTriangle,
  FileText,
  LineChart,
  ListChecks,
  Target,
  TrendingDown,
  TrendingUp,
} from "lucide-react";

import {
  AnomalyChip,
  BlockCard,
  cleanNarrative,
  EmptyNote,
  EvidenceCitation,
  ItemTag,
  NumberedList,
  type KpiMetricLookup,
} from "./KpiDetailBlocks";
import type {
  XFactorKpiCategoryDetailModel,
  XFactorKpiMetricRef,
  XFactorKpiTrendSource,
} from "../lib/deriveKpi";

const TREND_SOURCE_LABEL: Record<XFactorKpiTrendSource, string> = {
  trend_insight: "trend narrative",
  insight_trend_flag: "trend insight",
  metric_top_mover: "top mover",
  cross_category_rollup: "cross-category",
};

const BriefHeading: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div className="text-[10.5px] font-semibold uppercase tracking-wider text-purple-500">
    {children}
  </div>
);

const refsOf = (
  items: Array<{ supportingMetricRefs: XFactorKpiMetricRef[] }>
) => items.flatMap(i => i.supportingMetricRefs);

export type KpiPaneAnchorKey =
  | "synopsis"
  | "highlights"
  | "lowlights"
  | "opportunities"
  | "risks"
  | "actions"
  | "trends";

export const kpiPaneAnchorId = (scope: string, key: KpiPaneAnchorKey) =>
  `${scope}-${key}`;

type Props = {
  category: XFactorKpiCategoryDetailModel;
  lookup: KpiMetricLookup;
  measurementDate: string | null;
  /** Prefix for block anchor ids (unique per pane in Full Briefing). */
  scope?: string;
};

/**
 * Narrative blocks for one Overall/category pane — shared by category detail
 * and the multi-pane KPI Full Briefing (xfactor CategoryContent equivalent).
 */
const KpiCategoryPaneBody: React.FC<Props> = ({
  category,
  lookup,
  measurementDate,
  scope = "kpi-pane",
}) => {
  const id = (key: KpiPaneAnchorKey) => kpiPaneAnchorId(scope, key);

  return (
    <div className="grid gap-3">
      <BlockCard
        id={id("synopsis")}
        tone="blue"
        icon={FileText}
        title="Synopsis"
        right={
          <EvidenceCitation
            title="Synopsis"
            refs={category.synopsisRefs}
            lookup={lookup}
            measurementDate={measurementDate}
          />
        }
      >
        {category.synopsis ? (
          <p className="m-0 whitespace-pre-line leading-relaxed">
            {cleanNarrative(category.synopsis)}
          </p>
        ) : (
          <EmptyNote>Synopsis not available.</EmptyNote>
        )}
        <div className="mt-3 space-y-3 pl-3">
          <div className="space-y-1">
            <BriefHeading>Highlights &amp; Lowlights Brief</BriefHeading>
            {category.highlightsBrief ? (
              <p className="m-0 text-[12px] text-gray-500">
                {cleanNarrative(category.highlightsBrief)}
              </p>
            ) : null}
            {category.lowlightsBrief ? (
              <p className="m-0 text-[12px] text-gray-500">
                {cleanNarrative(category.lowlightsBrief)}
              </p>
            ) : null}
            {!category.highlightsBrief && !category.lowlightsBrief ? (
              <EmptyNote>
                Highlights &amp; lowlights brief not available.
              </EmptyNote>
            ) : null}
          </div>
          <div className="space-y-1">
            <BriefHeading>Risks &amp; Opportunities Brief</BriefHeading>
            {category.roBrief ? (
              <p className="m-0 text-[12px] text-gray-500">
                {cleanNarrative(category.roBrief)}
              </p>
            ) : (
              <EmptyNote>
                Risks &amp; opportunities brief not available.
              </EmptyNote>
            )}
          </div>
        </div>
      </BlockCard>

      <BlockCard
        id={id("highlights")}
        tone="emerald"
        icon={TrendingUp}
        title="Highlights"
        right={
          <EvidenceCitation
            title="Highlights"
            refs={refsOf(category.highlights)}
            lookup={lookup}
            measurementDate={measurementDate}
          />
        }
      >
        {category.highlights.length ? (
          <NumberedList
            items={category.highlights.map(h => (
              <>
                <div className="flex flex-wrap items-center gap-1">
                  <span>{h.text}</span>
                  <AnomalyChip anomaly={h.anomaly} />
                </div>
                {h.explanation ? (
                  <div className="mt-0.5 text-[12px] text-gray-500">
                    {h.explanation}
                  </div>
                ) : null}
              </>
            ))}
          />
        ) : (
          <p className="m-0 text-[12px] text-gray-500">No highlights.</p>
        )}
      </BlockCard>

      <BlockCard
        id={id("lowlights")}
        tone="rose"
        icon={TrendingDown}
        title="Lowlights"
        right={
          <EvidenceCitation
            title="Lowlights"
            refs={refsOf(category.lowlights)}
            lookup={lookup}
            measurementDate={measurementDate}
          />
        }
      >
        {category.lowlights.length ? (
          <NumberedList
            items={category.lowlights.map(l => (
              <>
                <div className="flex flex-wrap items-center gap-1">
                  <span>{l.text}</span>
                  <AnomalyChip anomaly={l.anomaly} />
                </div>
                {l.explanation ? (
                  <div className="mt-0.5 text-[12px] text-gray-500">
                    {l.explanation}
                  </div>
                ) : null}
              </>
            ))}
          />
        ) : (
          <p className="m-0 text-[12px] text-gray-500">No lowlights.</p>
        )}
      </BlockCard>

      <BlockCard
        id={id("opportunities")}
        tone="teal"
        icon={Target}
        title="Opportunities"
        right={
          <EvidenceCitation
            title="Opportunities"
            refs={refsOf(category.opportunities)}
            lookup={lookup}
            measurementDate={measurementDate}
          />
        }
      >
        {category.opportunities.length ? (
          <NumberedList
            items={category.opportunities.map((o, i) => (
              <div key={i} className="flex flex-wrap items-center gap-1.5">
                <span>{o.text}</span>
                <AnomalyChip anomaly={o.anomaly} />
              </div>
            ))}
          />
        ) : (
          <EmptyNote>No opportunities reported.</EmptyNote>
        )}
      </BlockCard>

      <BlockCard
        id={id("risks")}
        tone="rose"
        icon={AlertTriangle}
        title="Risks"
        right={
          <EvidenceCitation
            title="Risks"
            refs={refsOf(category.risks)}
            lookup={lookup}
            measurementDate={measurementDate}
          />
        }
      >
        {category.risks.length ? (
          <NumberedList
            items={category.risks.map((r, i) => (
              <div key={i} className="flex flex-wrap items-center gap-1.5">
                <span>{r.text}</span>
                {r.horizon ? <ItemTag>{r.horizon}</ItemTag> : null}
                <AnomalyChip anomaly={r.anomaly} />
              </div>
            ))}
          />
        ) : (
          <EmptyNote>No risks reported.</EmptyNote>
        )}
      </BlockCard>

      <BlockCard
        id={id("actions")}
        tone="purple"
        icon={ListChecks}
        title="Recommended Actions"
        right={
          <EvidenceCitation
            title="Recommended Actions"
            refs={refsOf(category.actions)}
            lookup={lookup}
            measurementDate={measurementDate}
          />
        }
      >
        {category.actions.length ? (
          <NumberedList
            items={category.actions.map(a => (
              <>
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="font-medium">{a.action}</span>
                  {a.actionScore != null ? (
                    <ItemTag>score: {Math.round(a.actionScore)}</ItemTag>
                  ) : null}
                  <AnomalyChip anomaly={a.anomaly} />
                </div>
                {a.expectedOutcome ? (
                  <p className="m-0 mt-1 text-[12px] text-gray-500">
                    <span className="font-semibold">Expected outcome:</span>{" "}
                    {a.expectedOutcome}
                  </p>
                ) : null}
                {a.actionExplanation ? (
                  <p className="m-0 mt-1 text-[12px]">
                    <span className="font-semibold">Rationale:</span>{" "}
                    {a.actionExplanation}
                  </p>
                ) : null}
              </>
            ))}
          />
        ) : (
          <EmptyNote>No key recommendations extracted.</EmptyNote>
        )}
      </BlockCard>

      <BlockCard
        id={id("trends")}
        tone="cyan"
        icon={LineChart}
        title="Key Trends"
        right={
          <EvidenceCitation
            title="Key Trends"
            refs={category.trends.flatMap(t =>
              t.citations
                .filter(c => lookup(c.split("@")[0]))
                .map(c => ({
                  metricId: c.split("@")[0],
                  period: c.includes("@") ? c.split("@")[1] : null,
                }))
            )}
            lookup={lookup}
            measurementDate={measurementDate}
          />
        }
      >
        {category.trends.length ? (
          <NumberedList
            items={category.trends.map((t, i) => {
              const marker = "Insufficient Data";
              const idx = t.text.indexOf(marker);
              return (
                <div key={i} className="flex flex-wrap items-center gap-1.5">
                  <span>
                    {idx < 0 ? (
                      t.text
                    ) : (
                      <>
                        {t.text.slice(0, idx)}
                        <em>{marker}</em>
                        {t.text.slice(idx + marker.length)}
                      </>
                    )}
                  </span>
                  <ItemTag>{TREND_SOURCE_LABEL[t.source]}</ItemTag>
                  {t.qualitative ? (
                    <ItemTag tone="amber">qualitative</ItemTag>
                  ) : null}
                </div>
              );
            })}
          />
        ) : (
          <EmptyNote>No trend insights available.</EmptyNote>
        )}
      </BlockCard>
    </div>
  );
};

export default KpiCategoryPaneBody;
